import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Stack, Button, TextField, MenuItem, Chip,
  Alert, FormControlLabel, Switch, CircularProgress,
  Tabs, Tab, Card, Grid, Tooltip, IconButton, alpha, useTheme,
  LinearProgress,
} from '@mui/material';
import {
  Sparkles, Clock, CheckCircle2,
  RefreshCw, Layers, ExternalLink, Zap, Settings,
  History, Check,
} from 'lucide-react';
import axios from '../utils/axios';
import { AdminDialog, AdminDialogHeader, AdminDialogContent, AdminDialogActions } from './common/modal';
import { getBlogMainImageUrl } from '../utils/helpers';

const MARKET_PILLARS = [
  {
    key: 'All',
    label: 'All B2B Market',
    icon: '🌐',
    desc: 'Cross-ecosystem B2B commerce',
  },
  {
    key: 'Wholesaler',
    label: 'Wholesaler',
    icon: '📦',
    desc: 'Bulk sourcing, distributors & mandis',
  },
  {
    key: 'Reseller',
    label: 'Reseller',
    icon: '🛍️',
    desc: 'Zero investment, WhatsApp selling & margins',
  },
  {
    key: 'Retail',
    label: 'Retail Shopkeeper',
    icon: '🏪',
    desc: 'Shopkeeper stock, retail margins & turnover',
  },
  {
    key: 'B2B Market',
    label: 'B2B & Manufacturing',
    icon: '🏭',
    desc: 'Direct factory connections, RFQs & MSME',
  },
];

const DEFAULT_MARKETPLACE_CATEGORIES = [
  "Wholesale & Bulk Sourcing",
  "Reseller & Social Selling",
  "Retail & Kirana Growth",
  "B2B Manufacturing & MSME",
  "Sourcing Mandis & Hubs",
  "WhatsApp & Social Commerce",
  "Profit Margins & Pricing Strategy",
  "Supplier Verification & Safe Trade",
  "Trending Products & High Margin Niches",
];

const PRESET_TOPICS_BY_MARKET = {
  All: [
    "How Indian Wholesalers Can Maximize Margins Sourcing from Surat & Delhi",
    "Top 10 High-Demand FMCG & Grocery Sourcing Secrets for Small Retailers",
    "The Complete Guide to WhatsApp Commerce for Manufacturers & Wholesalers",
    "How to Verify B2B Suppliers in India to Avoid Middleman Commissions & Scams",
    "Starting a Clothing Reselling Business from Home: Sourcing to First 100 Orders",
    "Factory Direct vs Middleman: How Reseller Bazzar Delivers 0% Commission Sourcing",
    "Zero-Inventory Dropshipping via Indian Manufacturers: What Actually Works",
  ],
  Wholesaler: [
    "How Indian Wholesalers Can Move Bulk Lots Fast Without 15-25% Marketplace Cuts",
    "Top Wholesale Mandis in Surat, Ahmedabad, and Sadar Bazaar Delhi: Sourcing Playbook",
    "Managing Working Capital, Credit Cycles, and Transporter Bilty for Indian Wholesalers",
    "How Wholesalers Can Build Verified Pan-India Dealer Networks on WhatsApp",
    "Calculating Tiered Bulk Pricing & MOQs: Sourcing, Packaging, and Delivery Formulas",
    "Tirupur Knitwear & Cotton Hosiery Wholesale: Sourcing Direct from Mill Gates",
    "Liquidating Slow-Moving B2B Stock: 5 Proven Strategies for Indian Stockists",
  ],
  Reseller: [
    "How to Start a Zero-Investment Clothing Reselling Business from Home in India",
    "Direct Factory vs Middleman: How Resellers Can Unlock 40-60% Margins on Reseller Bazzar",
    "The Ultimate WhatsApp Group & Broadcast Selling Blueprint for Social Resellers",
    "Sourcing High-Demand Ethnic Wear, Jewelry & Cosmetics Directly from Surat & Jaipur Factories",
    "Handling Customer Inquiries, COD vs Online Payments, and Returns for Independent Resellers",
    "Selling Artificial Jewellery on Instagram & WhatsApp: Sourcing from Rajkot & Delhi Sadar",
    "How Home Resellers Build VIP Customer Lists that Generate ₹50,000+ Monthly Profits",
  ],
  Retail: [
    "Kirana & Retail Store Sourcing: How Small Shopkeepers Can Get Tier-1 Distributor Rates",
    "Retail Profit Margin Masterclass: Sourcing Direct to Beat Big E-Commerce Discounts",
    "Festival & Wedding Season Stock Planning for Boutiques and Retail Showrooms",
    "Cash Flow & Dead Stock Secrets: How Indian Retailers Free Up Trapped Capital",
    "Electronics & Mobile Accessories Retail: Sourcing High-Quality Trending Gadgets in Bulk",
    "Beating Quick-Commerce Apps: How Neighborhood Kiranas Win on Personalization & Fresh Stock",
    "Agra Footwear Wholesale: How Shoe Retailers Source Directly from Leather & PU Factories",
  ],
  'B2B Market': [
    "How Indian MSMEs & Manufacturers Can Build Verified Digital Storefronts for Pan-India Buyers",
    "The Verified Supplier Playbook: Avoiding Payment Scams and Fraud in Indian B2B Trade",
    "B2B RFQ Bidding: How Indian Suppliers Can Win High-Value Corporate & Wholesale Purchase Orders",
    "Private Labeling & Contract Manufacturing in India: Sourcing Finished Goods with Your Brand",
    "Building High-Converting B2B Mobile Catalogs: Tips for Indian Industrial Traders",
    "GST Invoicing, E-Way Bills & Interstate B2B Logistics: The Compliant Indian Seller Guide",
    "Zero Commission vs 15% Portal Deductions: Real Profit Comparison for Indian Manufacturers",
  ],
};

export default function AutoBlogModal({ open, onClose, onBlogCreated }) {
  const theme = useTheme();
  const [tab, setTab] = useState(0);

  // Form state
  const [focusMarket, setFocusMarket] = useState('All');
  const [topicPrompt, setTopicPrompt] = useState('');
  const [category, setCategory] = useState('');
  const [customKeywords, setCustomKeywords] = useState('');

  // Generation state
  const [generating, setGenerating] = useState(false);
  const [genStep, setGenStep] = useState('');
  const [generatedResult, setGeneratedResult] = useState(null);
  const [error, setError] = useState('');

  // Settings & History state
  const [settings, setSettings] = useState(null);
  const [history, setHistory] = useState([]);
  const [loadingSettings, setLoadingSettings] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [settingsSavedToast, setSettingsSavedToast] = useState(false);

  const loadSettingsAndHistory = useCallback(async () => {
    setLoadingSettings(true);
    try {
      const [setRes, histRes] = await Promise.all([
        axios.get('/admin/blogs/auto/settings', { skipNotify: true }),
        axios.get('/admin/blogs/auto/history', { skipNotify: true }),
      ]);
      setSettings(setRes.data?.settings || {});
      setHistory(histRes.data?.history || []);
    } catch (err) {
      console.warn("Could not load auto-blog settings:", err);
    } finally {
      setLoadingSettings(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      setError('');
      setGeneratedResult(null);
      loadSettingsAndHistory();
    }
  }, [open, loadSettingsAndHistory]);

  const handleSurpriseMe = () => {
    const list = PRESET_TOPICS_BY_MARKET[focusMarket] || PRESET_TOPICS_BY_MARKET.All;
    const random = list[Math.floor(Math.random() * list.length)];
    setTopicPrompt(random);
  };

  const handleGenerateNow = async () => {
    setGenerating(true);
    setError('');
    setGeneratedResult(null);
    setGenStep(`Calling Gemini Pro to write structured blog for ${focusMarket === 'All' ? 'B2B Commerce' : focusMarket}...`);

    try {
      const res = await axios.post('/admin/blogs/auto/generate', {
        topicPrompt: topicPrompt.trim(),
        preferredCategory: category,
        customKeywords: customKeywords.trim(),
        focusMarket,
      }, {
        timeout: 90000, // 90s for LLM + Unsplash image processing
        skipNotify: true,
      });

      if (res.data?.blog) {
        setGeneratedResult(res.data.blog);
        onBlogCreated && onBlogCreated(res.data.blog);
        loadSettingsAndHistory();
      }
    } catch (err) {
      console.error("Auto blog generation failed:", err);
      setError(err.response?.data?.msg || err.message || 'Failed to generate blog with Gemini Pro');
    } finally {
      setGenerating(false);
      setGenStep('');
    }
  };

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      const res = await axios.put('/admin/blogs/auto/settings', {
        enabled: settings.enabled,
        model: settings.model,
        auto_publish: settings.auto_publish,
        categories: settings.categories,
        focus_markets: settings.focus_markets,
        target_audience: settings.target_audience,
        unsplash_access_key: settings.unsplash_access_key,
      }, { skipNotify: true });
      if (res.data?.settings) {
        setSettings(res.data.settings);
        setSettingsSavedToast(true);
        setTimeout(() => setSettingsSavedToast(false), 3000);
      }
    } catch (err) {
      setError(err.response?.data?.msg || 'Failed to update settings');
    } finally {
      setSavingSettings(false);
    }
  };

  return (
    <AdminDialog open={open} onClose={generating ? undefined : onClose} maxWidth="md" fullWidth>
      <AdminDialogHeader
        title={
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                width: 38,
                height: 38,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'linear-gradient(135deg, #fe5f01 0%, #f75d34 100%)',
                color: '#fff',
                boxShadow: '0 4px 14px rgba(254,95,1,0.3)',
              }}
            >
              <Sparkles size={20} />
            </Box>
            <Box>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="h6" sx={{ fontWeight: 800 }}>Auto Blog AI System</Typography>
                <Chip
                  label="Daily 2:00 PM IST"
                  size="small"
                  sx={{
                    fontWeight: 700,
                    bgcolor: alpha(theme.palette.success.main, 0.12),
                    color: theme.palette.success.dark,
                    fontSize: 11,
                    height: 22,
                  }}
                />
              </Stack>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Automated block-section blog creator powered by Gemini Pro & Unsplash
              </Typography>
            </Box>
          </Stack>
        }
        onClose={generating ? undefined : onClose}
      />

      <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3, pt: 1, bgcolor: 'background.neutral' }}>
        <Tabs value={tab} onChange={(e, v) => setTab(v)}>
          <Tab icon={<Zap size={15} style={{ marginRight: 6 }} />} iconPosition="start" label="Generate Now" />
          <Tab icon={<Settings size={15} style={{ marginRight: 6 }} />} iconPosition="start" label="Schedule & Settings" />
          <Tab icon={<History size={15} style={{ marginRight: 6 }} />} iconPosition="start" label="Generation History" />
        </Tabs>
      </Box>

      <AdminDialogContent sx={{ p: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2.5 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* ───────── TAB 0: GENERATE BLOG NOW ───────── */}
        {tab === 0 && (
          <Stack spacing={2.5}>
            {/* Status Hero Card */}
            <Card
              sx={{
                p: 2,
                borderRadius: 2.5,
                background: theme.palette.mode === 'dark'
                  ? 'rgba(255,255,255,0.03)'
                  : 'linear-gradient(135deg, #fff7ed 0%, #f8fafc 100%)',
                border: '1px solid',
                borderColor: theme.palette.mode === 'dark' ? 'divider' : '#fed7aa',
              }}
            >
              <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Box sx={{ color: '#fe5f01' }}>
                    <Clock size={24} />
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                      Automatic 2:00 PM Daily Schedule: {settings?.enabled !== false ? 'Active' : 'Paused'}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: 12.5 }}>
                      The system automatically runs every day at 14:00 IST to write, find Unsplash photos, and publish.
                    </Typography>
                  </Box>
                </Stack>
                <Chip
                  label={settings?.enabled !== false ? 'Daily Cron Active' : 'Cron Paused'}
                  color={settings?.enabled !== false ? 'success' : 'default'}
                  size="small"
                  sx={{ fontWeight: 700 }}
                />
              </Stack>
            </Card>

            {/* Generated Success Preview */}
            {generatedResult && (
              <Card
                sx={{
                  p: 2.5,
                  borderRadius: 3,
                  border: '2px solid #10b981',
                  bgcolor: alpha('#10b981', 0.04),
                }}
              >
                <Stack direction="row" spacing={2} alignItems="flex-start">
                  {generatedResult.blog_main_image && (
                    <Box
                      component="img"
                      src={getBlogMainImageUrl(generatedResult.blog_main_image)}
                      alt={generatedResult.title}
                      sx={{
                        width: 140,
                        height: 90,
                        borderRadius: 2,
                        objectFit: 'cover',
                        flexShrink: 0,
                      }}
                    />
                  )}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                      <Chip
                        icon={<CheckCircle2 size={13} />}
                        label="Successfully Created & Published"
                        color="success"
                        size="small"
                        sx={{ fontWeight: 700, fontSize: 11 }}
                      />
                      <Chip
                        icon={<Layers size={13} />}
                        label={`${generatedResult.blocks?.length || 0} Block Sections`}
                        size="small"
                        sx={{ fontWeight: 700, fontSize: 11 }}
                      />
                    </Stack>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, lineHeight: 1.3, mb: 0.5 }}>
                      {generatedResult.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: 12, mb: 1.5 }}>
                      Slug: /blog/{generatedResult.url} · Category: {generatedResult.category_name}
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      endIcon={<ExternalLink size={14} />}
                      onClick={() => window.open(`https://www.resellerbazzar.in/blog/${generatedResult.url}`, '_blank', 'noopener')}
                      sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 1.5 }}
                    >
                      View Live Blog
                    </Button>
                  </Box>
                </Stack>
              </Card>
            )}

            {/* Generation Form */}
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 800, textTransform: 'uppercase', color: 'text.secondary', letterSpacing: 0.5, mb: 1, display: 'block' }}>
                Target Market Focus Pillar
              </Typography>
              <Grid container spacing={1.25} sx={{ mb: 2 }}>
                {MARKET_PILLARS.map((m) => {
                  const isSelected = focusMarket === m.key;
                  return (
                    <Grid size={{ xs: 6, sm: 2.4 }} key={m.key}>
                      <Box
                        onClick={() => {
                          setFocusMarket(m.key);
                        }}
                        sx={{
                          p: 1.25,
                          borderRadius: 2,
                          border: '1.5px solid',
                          borderColor: isSelected ? '#fe5f01' : theme.palette.divider,
                          bgcolor: isSelected ? alpha('#fe5f01', 0.08) : 'background.paper',
                          cursor: 'pointer',
                          transition: 'all 0.15s ease',
                          textAlign: 'center',
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: isSelected ? '0 4px 12px rgba(254,95,1,0.18)' : 'none',
                          '&:hover': {
                            borderColor: '#fe5f01',
                            transform: 'translateY(-1px)',
                          },
                        }}
                      >
                        <Typography variant="body2" sx={{ fontSize: 20, mb: 0.25 }}>{m.icon}</Typography>
                        <Typography variant="subtitle2" sx={{ fontWeight: 800, fontSize: 12, color: isSelected ? '#fe5f01' : 'text.primary', lineHeight: 1.2 }}>
                          {m.label}
                        </Typography>
                        <Typography variant="caption" sx={{ fontSize: 10, color: 'text.secondary', display: { xs: 'none', sm: 'block' }, mt: 0.25 }}>
                          {m.desc.split(',')[0]}
                        </Typography>
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>

              {/* Dynamic Quick Presets for Selected Market */}
              <Box sx={{ mb: 1.5, p: 1.5, bgcolor: alpha(theme.palette.primary.main, 0.04), borderRadius: 2, border: '1px dashed', borderColor: alpha(theme.palette.primary.main, 0.3) }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.75 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700 }}>
                    💡 Suggested {focusMarket === 'All' ? 'B2B Commerce' : focusMarket} Topics:
                  </Typography>
                  <Button
                    size="small"
                    variant="text"
                    startIcon={<Sparkles size={13} />}
                    onClick={handleSurpriseMe}
                    disabled={generating}
                    sx={{ textTransform: 'none', fontWeight: 700, fontSize: 11.5, color: '#fe5f01', p: 0 }}
                  >
                    Random {focusMarket} Topic
                  </Button>
                </Stack>
                <Stack direction="row" spacing={0.75} flexWrap="wrap" gap={0.75}>
                  {(PRESET_TOPICS_BY_MARKET[focusMarket] || PRESET_TOPICS_BY_MARKET.All).slice(0, 3).map((t, idx) => (
                    <Chip
                      key={idx}
                      label={t}
                      size="small"
                      onClick={() => setTopicPrompt(t)}
                      sx={{
                        fontSize: 11.5,
                        cursor: 'pointer',
                        maxWidth: '100%',
                        bgcolor: topicPrompt === t ? '#fe5f01' : 'background.paper',
                        color: topicPrompt === t ? '#ffffff' : 'text.primary',
                        borderColor: topicPrompt === t ? '#fe5f01' : theme.palette.divider,
                        fontWeight: topicPrompt === t ? 700 : 500,
                        '&:hover': {
                          borderColor: '#fe5f01',
                        },
                      }}
                      variant={topicPrompt === t ? 'filled' : 'outlined'}
                    />
                  ))}
                </Stack>
              </Box>

              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                  Custom Topic / Headline Prompt
                </Typography>
              </Stack>
              <TextField
                fullWidth
                multiline
                rows={2}
                placeholder={`Leave blank for AI to generate a fresh, unique title automatically, or enter a topic for ${focusMarket === 'All' ? 'wholesalers, resellers, retail, and B2B' : focusMarket}...`}
                value={topicPrompt}
                onChange={(e) => setTopicPrompt(e.target.value)}
                disabled={generating}
              />
            </Box>

            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  select
                  fullWidth
                  label="Category Niche"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  disabled={generating}
                >
                  <MenuItem value="">
                    <em>✨ Auto (AI selects best marketplace category)</em>
                  </MenuItem>
                  {(settings?.categories && settings.categories.length > 0 ? settings.categories : DEFAULT_MARKETPLACE_CATEGORIES).map((cat) => (
                    <MenuItem key={cat} value={cat}>
                      {cat}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Target SEO Keywords (Optional)"
                  placeholder="e.g. wholesale surat, b2b reselling"
                  value={customKeywords}
                  onChange={(e) => setCustomKeywords(e.target.value)}
                  disabled={generating}
                />
              </Grid>
            </Grid>

            {/* Generating progress indicator */}
            {generating && (
              <Box sx={{ p: 2, borderRadius: 2, bgcolor: alpha('#fe5f01', 0.08), border: '1px solid #fe5f01' }}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <CircularProgress size={24} sx={{ color: '#fe5f01' }} />
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#fe5f01' }}>
                      Generating Blog with Gemini Pro & Unsplash...
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {genStep || 'Constructing structured block sections (Intro, Takeaways, H2s, Callouts, FAQs, CTAs)...'}
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            )}

            <Button
              variant="contained"
              size="large"
              startIcon={generating ? <CircularProgress size={18} color="inherit" /> : <Sparkles size={18} />}
              onClick={handleGenerateNow}
              disabled={generating}
              sx={{
                py: 1.5,
                background: 'linear-gradient(135deg, #fe5f01 0%, #f75d34 100%)',
                color: '#fff',
                fontWeight: 800,
                fontSize: '0.95rem',
                borderRadius: 2,
                boxShadow: '0 6px 20px rgba(254,95,1,0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #ea5501 0%, #e04e28 100%)',
                },
              }}
            >
              {generating ? 'Writing & Uploading Image...' : 'Generate & Publish Blog Now with Gemini Pro'}
            </Button>
          </Stack>
        )}

        {/* ───────── TAB 1: SCHEDULE & SETTINGS ───────── */}
        {tab === 1 && (
          <Stack spacing={3}>
            {loadingSettings && <LinearProgress />}
            {settingsSavedToast && (
              <Alert severity="success" icon={<Check size={18} />}>
                Settings saved successfully!
              </Alert>
            )}

            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
                Automated Schedule
              </Typography>
              <Card sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings?.enabled !== false}
                      onChange={(e) => setSettings({ ...settings, enabled: e.target.checked })}
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography sx={{ fontWeight: 700, fontSize: 14 }}>
                        Daily 2:00 PM IST Blog Writer
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                        Fires automatically at 14:00 (Asia/Kolkata). Writes article using Gemini Pro, pulls matching Unsplash image, formats block sections, and saves.
                      </Typography>
                    </Box>
                  }
                />
              </Card>
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
                AI Model & Publishing
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    select
                    fullWidth
                    label="Gemini AI Model"
                    value={settings?.model || 'gemini-3.1-pro-preview'}
                    onChange={(e) => setSettings({ ...settings, model: e.target.value })}
                  >
                    <MenuItem value="gemini-3.1-pro-preview">Gemini 3.1 Pro (Recommended)</MenuItem>
                    <MenuItem value="gemini-pro-latest">Gemini Pro Latest</MenuItem>
                    <MenuItem value="gemini-3.1-flash-lite">Gemini Flash Lite (Fast)</MenuItem>
                  </TextField>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    select
                    fullWidth
                    label="Publication Mode"
                    value={settings?.auto_publish ? 'published' : 'draft'}
                    onChange={(e) => setSettings({ ...settings, auto_publish: e.target.value === 'published' })}
                  >
                    <MenuItem value="published">Publish Immediately (Visible on Site)</MenuItem>
                    <MenuItem value="draft">Save as Draft (Review in Admin First)</MenuItem>
                  </TextField>
                </Grid>
              </Grid>
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>
                Automated Focus Pillars (Daily Cron Rotation)
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1.5 }}>
                Select which market segments the daily 2:00 PM automated cron rotates through:
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                {[
                  { key: 'Wholesaler', label: 'Wholesalers & Distributors 📦' },
                  { key: 'Reseller', label: 'Independent Resellers 🛍️' },
                  { key: 'Retail', label: 'Retail Shopkeepers 🏪' },
                  { key: 'B2B Market', label: 'B2B & Manufacturers 🏭' },
                ].map((pillar) => {
                  const currentMarkets = settings?.focus_markets || ['Wholesaler', 'Reseller', 'Retail', 'B2B Market'];
                  const isChecked = currentMarkets.includes(pillar.key);
                  return (
                    <Chip
                      key={pillar.key}
                      label={pillar.label}
                      clickable
                      color={isChecked ? 'primary' : 'default'}
                      variant={isChecked ? 'filled' : 'outlined'}
                      onClick={() => {
                        const next = isChecked
                          ? currentMarkets.filter((m) => m !== pillar.key)
                          : [...currentMarkets, pillar.key];
                        setSettings({ ...settings, focus_markets: next.length > 0 ? next : [pillar.key] });
                      }}
                      sx={{ fontWeight: 700, px: 0.5 }}
                    />
                  );
                })}
              </Stack>
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>
                Unsplash Image Sourcing
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1.5 }}>
                The system automatically searches, downloads, and converts high-res 1200x675 WebP images to S3/local storage. You can optionally add your own Unsplash API Access Key.
              </Typography>
              <TextField
                fullWidth
                label="Unsplash Access Key (Optional)"
                placeholder="Leave blank to use direct high-res curated Unsplash image downloader"
                value={settings?.unsplash_access_key || ''}
                onChange={(e) => setSettings({ ...settings, unsplash_access_key: e.target.value })}
                helperText="Find in Unsplash Developer Dashboard (https://unsplash.com/developers). If blank, built-in high-res sourcing is used."
              />
            </Box>

            <Button
              variant="contained"
              onClick={handleSaveSettings}
              disabled={savingSettings}
              sx={{ alignSelf: 'flex-start', px: 4, fontWeight: 700, borderRadius: 2 }}
            >
              {savingSettings ? 'Saving...' : 'Save Settings'}
            </Button>
          </Stack>
        )}

        {/* ───────── TAB 2: GENERATION HISTORY ───────── */}
        {tab === 2 && (
          <Stack spacing={2}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                Recent Auto-Generated Blogs ({history.length})
              </Typography>
              <Button
                size="small"
                startIcon={<RefreshCw size={14} />}
                onClick={loadSettingsAndHistory}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                Refresh
              </Button>
            </Stack>

            {history.length === 0 ? (
              <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
                <History size={32} style={{ opacity: 0.5, marginBottom: 8 }} />
                <Typography variant="body2">No auto-generated blogs recorded yet.</Typography>
              </Box>
            ) : (
              history.map((item, idx) => (
                <Card
                  key={idx}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box sx={{ minWidth: 0, flex: 1, pr: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800 }} noWrap>
                        {item.title}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {new Date(item.created_at).toLocaleString('en-IN')} · {item.category || 'General'}
                        {item.target_market && ` · Focus: ${item.target_market}`}
                        {item.slug && ` · /blog/${item.slug}`}
                      </Typography>
                      {item.error && (
                        <Typography variant="caption" color="error" sx={{ display: 'block' }}>
                          Error: {item.error}
                        </Typography>
                      )}
                    </Box>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Chip
                        label={item.status || 'published'}
                        size="small"
                        color={item.status === 'failed' ? 'error' : item.status === 'draft' ? 'warning' : 'success'}
                        sx={{ fontWeight: 700, fontSize: 11 }}
                      />
                      {item.slug && (
                        <Tooltip title="View blog on site">
                          <IconButton
                            size="small"
                            onClick={() => window.open(`https://www.resellerbazzar.in/blog/${item.slug}`, '_blank', 'noopener')}
                          >
                            <ExternalLink size={15} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  </Stack>
                </Card>
              ))
            )}
          </Stack>
        )}
      </AdminDialogContent>

      <AdminDialogActions>
        <Button onClick={onClose} color="inherit">
          Close
        </Button>
      </AdminDialogActions>
    </AdminDialog>
  );
}
